import { Component, OnInit, Injector } from '@angular/core';
import { SearchObject } from '../../services/Rest';
import { BaseComponent } from '../base/base.component';
import { Printer, Store } from '../../models/RestModels';
import { forkJoin } from 'rxjs';
import { PrinterService, LocalPrinterPreferences } from '../../services/printer.service';

interface USBDevice {
	vendorId: number;
	productId: number;
	serialNumber?: string;
	productName?: string;
	manufacturerName?: string;
}

@Component({
	selector: 'app-list-printer-config',
	templateUrl: './list-printer-config.component.html',
	styleUrls: ['./list-printer-config.component.css']
})
export class ListPrinterConfigComponent extends BaseComponent implements OnInit {

	printer_search: SearchObject<Printer> = this.getEmptySearch();
	printer_list: Printer[] = [];
	store_list: Store[] = [];
	store_map: Map<number, Store> = new Map();
	preferences: LocalPrinterPreferences = {
		print_method: 'BROWSER',
		pos_printer_id: null,
		kitchen_printer_ids: []
	};
	paired_devices: USBDevice[] = [];
	private printerService: PrinterService;

	constructor(public injector: Injector) {
		super(injector);
		this.printerService = injector.get(PrinterService);
	}

	ngOnInit() {
		this.path = '/list-printer-config';

		this.subs.sink = this.route.queryParamMap.subscribe((queryParamMap) => {
			let fields = ["id", "store_id", "name", "model", "font_a_columns", "font_b_columns", "compact_items", "calculation_style", "created", "updated"];
			let extra_keys: Array<string> = ['store_id'];
			this.printer_search = this.getSearch(queryParamMap, fields, extra_keys);
			this.printer_search.limit = 999999;

			this.setTitle('Impresoras USB');

			this.current_page = this.printer_search.page;
			this.is_loading = true;

			this.subs.sink = forkJoin({
				printers: this.rest.printer.search(this.printer_search),
				stores: this.rest.store.search({ limit: 9999, sort_order: ['name_ASC'] })
			}).subscribe(
				(responses) => {
					this.setPages(this.printer_search.page, responses.printers.total);
					this.printer_list = responses.printers.data;
					this.store_list = responses.stores.data;

					this.store_map.clear();
					for (const store of this.store_list) {
						if (store.id) {
							this.store_map.set(store.id, store);
						}
					}

					this.preferences = this.printerService.loadLocalPreferences();
					this.loadPairedDevices();

					this.is_loading = false;
				},
				(error) => {
					this.showError(error);
					this.is_loading = false;
				}
			);
		});
	}

	getStoreName(store_id: number): string {
		const store = this.store_map.get(store_id);
		return store?.name || 'Sin sucursal';
	}

	isPaired(printer: Printer): boolean {
		return this.preferences.pos_printer_id === printer.id ||
			this.preferences.kitchen_printer_ids.includes(printer.id);
	}

	getPairingType(printer: Printer): string {
		const types: string[] = [];
		if (this.preferences.pos_printer_id === printer.id) {
			types.push('POS');
		}
		if (this.preferences.kitchen_printer_ids.includes(printer.id)) {
			types.push('Cocina');
		}
		return types.join(', ');
	}

	isPosPrinter(printer: Printer): boolean {
		return this.preferences.pos_printer_id === printer.id;
	}

	isKitchenPrinter(printer: Printer): boolean {
		return this.preferences.kitchen_printer_ids.includes(printer.id);
	}

	togglePosPrinter(printer: Printer): void {
		if (this.preferences.pos_printer_id === printer.id) {
			this.preferences.pos_printer_id = null;
		} else {
			this.preferences.pos_printer_id = printer.id;
			if (this.preferences.print_method === 'BROWSER') {
				this.preferences.print_method = 'USB';
			}
		}
		this.savePreferences();
	}

	toggleKitchenPrinter(printer: Printer): void {
		const idx = this.preferences.kitchen_printer_ids.indexOf(printer.id);
		if (idx >= 0) {
			this.preferences.kitchen_printer_ids.splice(idx, 1);
		} else {
			this.preferences.kitchen_printer_ids.push(printer.id);
			if (this.preferences.print_method === 'BROWSER') {
				this.preferences.print_method = 'USB';
			}
		}
		this.savePreferences();
	}

	savePreferences(): void {
		this.printerService.saveLocalPreferences(this.preferences);
	}

	async loadPairedDevices(): Promise<void> {
		try {
			if ('usb' in navigator) {
				const devices = await (navigator as any).usb.getDevices();
				this.paired_devices = devices.map((d: any) => ({
					vendorId: d.vendorId,
					productId: d.productId,
					serialNumber: d.serialNumber,
					productName: d.productName,
					manufacturerName: d.manufacturerName
				}));
			}
		} catch (error) {
			console.warn('Could not load paired USB devices:', error);
		}
	}

	async pairNewDevice(event: Event): Promise<void> {
		try {
			if (!('usb' in navigator)) {
				this.showError('WebUSB no esta disponible en este navegador');
				return;
			}
			const device = await (navigator as any).usb.requestDevice({ filters: [] });
			await this.loadPairedDevices();
			this.showSuccess(`Impresora "${device.productName}" emparejada`);
		} catch (error: any) {
			if (error.name !== 'NotFoundError') {
				this.showError('Error al emparejar: ' + (error.message || error));
			}
		}
	}

	isDevicePaired(printer: Printer): boolean {
		const vendorId = printer.vendor_id ? parseInt(printer.vendor_id) : 0;
		const productId = printer.product_id ? parseInt(printer.product_id) : 0;
		return this.paired_devices.some(d =>
			d.vendorId === vendorId && d.productId === productId
		);
	}

	isDeviceConfigured(device: USBDevice): boolean {
		return this.printer_list.some(p => {
			const vendorId = p.vendor_id ? parseInt(p.vendor_id) : 0;
			const productId = p.product_id ? parseInt(p.product_id) : 0;
			return vendorId === device.vendorId && productId === device.productId;
		});
	}

	formatVendorId(id: number): string {
		return '0x' + id.toString(16).padStart(4, '0');
	}

	deletePrinter(printer: Printer) {
		this.subs.sink = this.confirmation.showConfirmAlert
		(
			printer,
			`Eliminar impresora`,
			`Esta seguro de querer eliminar la impresora ${printer.name} ? Esta accion no se puede deshacer`, 'Eliminar')
			.subscribe((confirmed) => {
				if (confirmed) {
					this.is_loading = true;
					this.subs.sink = this.rest.printer.delete(printer).subscribe(
						() => {
							this.printer_list = this.printer_list.filter(p => p.id !== printer.id);
							this.showSuccess('Impresora eliminada');
							this.is_loading = false;
						},
						(error) => {
							this.showError('Error al eliminar: ' + error?.message);
							this.is_loading = false;
						}
					);
				}
			});
	}
}
